/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import { GenericType } from "@/types";
import { useEffect, useMemo, useState } from "react";
type DataItemType = Record<string, GenericType>;
type UseReactTableType = {
  data?: Array<DataItemType>;
  avoid?: Array<string>;
  view?: Array<string>;
  visible?: Array<string>;
  verifyKey?: string;
  columnWidth?: number;
  columnSize?: { [key: string]: number };
};

type TableHeaderItem = {
  id: string;
  header: string;
  accessorKey: string;
  hidden: boolean;
  width: number;
  minWidth: number;
  maxWidth: number;
};

type TableHeaderType = Array<TableHeaderItem> | undefined;
type TableKeyType = Array<string> | undefined;
type TableType = GenericType;

type SelectionType = {
  activeSelection: Array<string>;
  count: number;
  rowCount: number;
  isAllSelection: boolean;
};

type ColumnSize = {
  width: number;
  height: number;
};

type ResizeType = {
  size: ColumnSize;
  key: string;
};

type UseReactTableReturnType = {
  tableHeader: TableHeaderType;
  tableKeys: TableKeyType;
  table: TableType;
  toggleSelection: () => void;
  rowSelection: SelectionType;
  onResize: (prop: ResizeType) => void;
  onSorting: (prop: GenericType) => void;
  loading: boolean;
};

export const useTable = ({
  data = [],
  avoid,
  view,
  visible,
  verifyKey,
  columnWidth = 200,
  columnSize,
}: UseReactTableType): UseReactTableReturnType => {
  const [rowSelection, setRowSelection] = useState<SelectionType>({
    activeSelection: [],
    count: 0,
    rowCount: 0,
    isAllSelection: false,
  });

  const [mutedData, setMutedData] = useState<GenericType>([]);
  const [loading, setLoading] = useState<boolean>(true);

  //Handle Setting Cookies
  const getCookies = () => {
    try {
      if (typeof window !== "undefined" && verifyKey) {
        const tempCacheData = localStorage.getItem(verifyKey);
        return tempCacheData ? JSON.parse(tempCacheData) : null;
      }
      return undefined;
    } catch (error) {
      return undefined;
    }
  };
  const setCookies = (prop: GenericType) => {
    if (typeof window !== "undefined" && verifyKey) {
      localStorage.setItem(verifyKey, JSON.stringify(prop));
    }
  };

  //Handle Sorting
  const onSorting = (prop: GenericType) => {
    const tempSorting = prop.map((item: DataItemType) => {
      const findData = tableHeader.find((items) => items.accessorKey === item);
      return {
        accessorKey: findData?.accessorKey,
        width: findData?.width,
      };
    });
    setCookies(tempSorting);
    setMutedData(prop);
  };

  //Resize handle
  const onResize = ({ key, size }: ResizeType) => {
    const tempResize = tableHeader.map((item: DataItemType) => {
      if (item.accessorKey === key) {
        return {
          accessorKey: item.accessorKey,
          width: size.width,
        };
      }
      return {
        accessorKey: item.accessorKey,
        width: item.width,
      };
    });
    setCookies(tempResize);
    setMutedData(tempResize);
  };

  //Check Value Type
  function getType(input: GenericType): string {
    if (Array.isArray(input)) {
      return "array";
    } else if (input === null) {
      return "null";
    } else {
      return typeof input;
    }
  }

  //Toggle Selection
  const toggleSelection = () => {
    const allId = data.map((item) => item.id);
    if (rowSelection.activeSelection.length === allId.length) {
      setRowSelection((prev) => ({
        ...prev,
        activeSelection: [],
        isAllSelection: false,
      }));
    } else {
      setRowSelection((prev) => ({
        ...prev,
        activeSelection: allId,
        isAllSelection: true,
      }));
    }
  };

  //Make Selection
  const makeSelection = (prop: string) => {
    let tempSelection = structuredClone(rowSelection.activeSelection);
    if (tempSelection.includes(prop)) {
      const temp = tempSelection.filter((item: string) => item !== prop);
      tempSelection = temp;
    } else {
      tempSelection = [...tempSelection, prop];
    }
    setRowSelection({
      ...rowSelection,
      activeSelection: tempSelection,
      count: tempSelection.length,
      isAllSelection: tempSelection.length === data.length,
    });
  };

  const sortingContent = (
    dataKeyA: GenericType,
    sortingData: GenericType,
  ): Array<GenericType> => {
    if (sortingData) {
      const dataKeyB = sortingData
        .map((item: GenericType) => item.accessorKey)
        .filter((sortingChildData: string) =>
          dataKeyA.includes(sortingChildData),
        );

      const sortedData = dataKeyB.concat(
        dataKeyA.filter((item: string) => !dataKeyB.includes(item)),
      );

      return sortedData;
    }
    return dataKeyA;
  };

  //All Visible Keys
  const tableKeys = useMemo(() => {
    const temp: GenericType[] = getCookies() || [];
    const tempNewKey = temp.map((Item) => Item.accessorKey);
    const tempKeys = data?.[0]
      ? Object.keys(data[0])
      : view && view.length > 0
        ? view
        : tempNewKey;
    let tempList = [];
    if (avoid) {
      const tempSortingData = tempKeys
        .filter((item) => !avoid.includes(item))
        .filter((item) => (view ? view?.includes(item) : true));

      tempList = sortingContent(tempSortingData, temp);
    } else {
      tempList = sortingContent(tempKeys, temp);
    }

    if (temp.length === 0 && view) {
      return view.filter((item) => tempList.includes(item));
    } else {
      return tempList;
    }
  }, [data, avoid, mutedData]);

  //Modify total count when data is change
  useEffect(() => {
    setRowSelection((prev) => ({ ...prev, rowCount: data.length }));
  }, [data.length]);

  //Header of table
  const tableHeader = useMemo(() => {
    const temp = getCookies();
    const temptableKeys = tableKeys.map((item) => {
      const hiddenItem = visible ? !visible.includes(item) : false;
      const getCurrentCache = temp?.find(
        (cacheItem: { accessorKey: "string"; width: "number" }) =>
          cacheItem.accessorKey === item,
      );
      const label =
        item.replaceAll("_", " ")[0].toUpperCase() +
        item.replaceAll("_", " ").substring(1);
      return {
        id: item,
        header: label,
        accessorKey: item,
        hidden: hiddenItem,
        width: getCurrentCache?.width || columnSize?.[item] || columnWidth,
        minWidth: 50,
        maxWidth: 400,
      };
    });
    setCookies(
      temptableKeys.map((item: DataItemType) => ({
        accessorKey: item.accessorKey,
        width: item.width,
      })),
    );
    return temptableKeys.filter((item2) => !item2.hidden);
  }, [tableKeys]);

  //Get All Table
  const table = useMemo(() => {
    return data.map((row: GenericType, index: number) => {
      const rowData = tableHeader.map((element) => {
        return {
          key: element.accessorKey,
          rowId: row.id || index,
          value: row[element.accessorKey],
          label: element.header,
          width: element.width,
          header: element,
          type: getType(row[element.accessorKey]),
        };
      });

      return {
        id: row.id || index,
        data: rowData,
        originData: row,
        makeSelection,
      };
    });
  }, [tableHeader, data]);

  useEffect(() => {
    setLoading(false);
  }, [table]);

  return {
    table,
    tableHeader,
    tableKeys,
    toggleSelection,
    rowSelection,
    onResize,
    onSorting,
    loading,
  };
};
